import React, { useState, useEffect } from 'react';
import PageHeader from '../../../components/common/PageHeader/PageHeader';
import ArchivePopup from '../../../components/Popup/ArchivePopup/ArchivePopup';
import DeletePopup from '../../../components/Popup/DeletePopup/DeletePopup';
import EditIcon from '../../../components/Table/EditIcon/EditIcon';
import DeleteIcon from '../../../components/Table/DeleteIcon/DeleteIcon';
import ArchiveIcon from '../../../components/Table/ArchiveIcon/ArchiveIcon';
import DetailIcon from '../../../components/Table/DetailIcon/DetailIcon';
import ExportLabel from '../../../components/common/ExportLabel/Export';
import AddButton from '../../../components/common/AddButton/AddButton';
import Filter from '../../../components/common/Filter/Filter';
import Sort from '../../../components/common/Sort/Sort';
import '../../../components/common/SubNavbar.css';
import '../../../components/common/CommonText.css';
import '../../../components/common/Table.css';
import '../../../pages/Faq/Faqs.css';
import './PlanFeatures.css';
import ArchiveSelectedItem from '../../../components/common/ArchiveSelectedItemLabel/ArchiveSelectedItems';
import SimpleSearchBox from '../../../components/common/Search/SimpleSearch';
import { baseurl } from '../../../api';
import { useCheckboxClick } from '../../../checkboxclick';
import useFilterStore from '../../../store/useFilterStore';
import { useNavigate } from 'react-router-dom';
import useRefreshStore from '../../../store/useRefreshStore';
import { fetchFeatures, archiveFeature, archiveFeaturesBulk, deleteFeature } from '../../../services/planfeatures/Featuresapi';
import { ArchiveAlert } from '../../../components/common/AlertBox/ArchiveAlert';
import { SuccessAlert } from '../../../components/common/AlertBox/GreenAlert';

const pageKey = "features";

const Features = () => {
  const { setFilters, getFilters } = useFilterStore();
  const savedFilters = getFilters(pageKey);
  const [items, setItems] = useState([]);
  const [isArchivePopupOpen, setIsArchivePopupOpen] = useState(false);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchParams, setSearchParams] = useState(savedFilters?.searchParams || { term: "" });
  const [selectedFilterOption, setSelectedFilterOption] = useState(savedFilters?.selectedFilterOption || "Unarchived");
  const [selectedSortOption, setSelectedSortOption] = useState(savedFilters?.selectedSortOption || "Newest");
  const { isParentSelected, selectedItems, handleParentCheckboxChange, handleChildCheckboxChange, resetCheckboxState } = useCheckboxClick(items);
  const navigate = useNavigate();
  const { refreshKeys } = useRefreshStore();
  const refreshKey = refreshKeys["Features"] || 0;
  const [exportState, setExportState] = useState({ isExporting: false, error: null });

  const sortOptions = ['A-Z', 'Z-A', 'Newest', 'Oldest'];
  const filterOptions = ['Default', 'Archived', 'Unarchived'];

  const mapSortOptionToQuery = (option) => {
    switch (option) {
      case 'A-Z': return 'a_to_z';
      case 'Z-A': return 'z_to_a';
      case 'Newest': return 'newest';
      case 'Oldest': return 'oldest';
      default: return 'newest';
    }
  };

  const loadFeatures = async () => {
    const data = await fetchFeatures({
      search: searchParams.term,
      filter: selectedFilterOption,
      sort: mapSortOptionToQuery(selectedSortOption),
    });
    setItems(data);
  };

  useEffect(() => { loadFeatures(); }, [refreshKey]);

  useEffect(() => {
    loadFeatures();
    setFilters(pageKey, { searchParams, selectedFilterOption, selectedSortOption });
  }, [searchParams, selectedFilterOption, selectedSortOption]);

  const handleArchiveSelectedItems = async () => {
    const { success } = await archiveFeaturesBulk(selectedItems);

    if (success) {
      loadFeatures();
      resetCheckboxState();
      closePopups();

      ArchiveAlert("Selected features updated successfully", "success");
    }
  };

  const handleArchive = async () => {
    const { success } = await archiveFeature(selectedItem.id);

    if (success) {
      await loadFeatures();
      closePopups();

      ArchiveAlert(
        `Feature ${selectedItem.is_archived ? "unarchived" : "archived"} successfully`,
        "success"
      );
    }
  };

  const handleDeleteFeature = async () => {
    const { success } = await deleteFeature(selectedItem.id);
    if (success) { loadFeatures(); closePopups(); 
      SuccessAlert("Feature deleted successfully")
    }
  };

  const handleEditClick = (item) => navigate(`/features/edit/${item.id}`);
  const handleDetailClick = (item) => navigate(`/features/detail/${item.id}`);
  const handleArchiveClick = (item) => { setSelectedItem(item); setIsArchivePopupOpen(true); };
  const handleDeleteClick = (item) => { setSelectedItem(item); setIsDeletePopupOpen(true); };
  const handleNewClick = () => navigate('/features/add');
  const handleSearchSelect = (term) => setSearchParams({ term });
  const handleSortOptionSelect = (option) => setSelectedSortOption(option);
  const handleFilterSelect = (option) => setSelectedFilterOption(option);

  const closePopups = () => {
    setIsArchivePopupOpen(false);
    setIsDeletePopupOpen(false);
    setSelectedItem(null);
  };

  const deletePopupFields = [
    { label: 'Are you sure you want to delete this record? This action cannot be undone.', type: 'text' },
  ];

  const handleExportStart = () => setExportState({ isExporting: true, error: null });
  const handleExportComplete = () => { setExportState({ isExporting: false, error: null }); resetCheckboxState(); };
  const handleExportError = (error) => setExportState({ isExporting: false, error });

  return (
    <div className="whole-container">
      <div className="top-heading">
        <PageHeader pageName="Plans /" nestedpageName="Features" linkTo="/our-plans" />
        <div className="export-container">
          <ArchiveSelectedItem
            selectedItems={selectedItems}
            items={items}
            onArchiveSelected={handleArchiveSelectedItems}
          />
          <ExportLabel
            exportUrl={`${baseurl}plans/feature/export/`}
            selectedIds={selectedItems}
            onExportStart={handleExportStart}
            onExportComplete={handleExportComplete}
            onExportError={handleExportError}
          />
        </div>
      </div>

      <div className="pagename-container">
        <div className="page-name">
          <h1 className="common-text fts-22 ftw-6 blue">Features</h1>
        </div>
        <div className="pagename-right-container">
          <AddButton onClick={handleNewClick} />
          <SimpleSearchBox placeholder="Search Features" onSearchSelect={handleSearchSelect} defaultValue={searchParams?.term} />
          <Filter options={filterOptions} onFilterSelect={handleFilterSelect} defaultValue={selectedFilterOption} />
          <Sort options={sortOptions} onOptionSelect={handleSortOptionSelect} defaultValue={selectedSortOption} />
        </div>
      </div>

      <div className='table-container'>
        <table className='common-table'>
          <thead className='common-table-head'>
            <tr className='common-table-thead-tr'>
              <th>
                <input
                  className="table-selectbox table-parent-selectbox"
                  type="checkbox"
                  checked={isParentSelected}
                  onChange={handleParentCheckboxChange}
                />Feature NAME
              </th>
              <th className='w-65'>Description</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody className="common-table-tbody">
            {items.length > 0 ? (
              items.map((feature) => (
                <tr key={feature.id} className={feature.is_archived ? 'archived-item' : ''}>
                  <td className='d-flex ai-start'>
                    <div>
                      <input
                        className="table-selectbox table-child-selectbox"
                        type="checkbox"
                        checked={selectedItems.includes(feature.id)}
                        onChange={() => handleChildCheckboxChange(feature.id)}
                      />
                    </div>
                    <div className='d-flex ai-center mr-30'>{feature.name}</div>
                  </td>
                  <td>
                    <div className='d-flex ai-center mr-30'>{feature.description}</div>
                  </td>
                  <td className="actions special-action">
                    <DetailIcon onClick={() => handleDetailClick(feature)} />
                    {feature.is_system ? (
                      <div className="actions special-action disabled-actions">
                        <EditIcon />
                        <ArchiveIcon isArchived={feature.is_archived} />
                        <DeleteIcon />
                      </div>
                    ) : (
                      <>
                        <EditIcon onClick={() => handleEditClick(feature)} />
                        <ArchiveIcon
                          onClick={() => handleArchiveClick(feature)}
                          isArchived={feature.is_archived}
                        />
                        <DeleteIcon onClick={() => handleDeleteClick(feature)} />
                      </>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="3">No Features Available</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <ArchivePopup
        isOpen={isArchivePopupOpen}
        isArchived={selectedItem?.is_archived}
        archivename={selectedItem?.name}
        onSave={handleArchive}
        onClose={() => setIsArchivePopupOpen(false)}
      />

      <DeletePopup
        isOpen={isDeletePopupOpen}
        title="Delete Feature"
        fields={deletePopupFields}
        onSave={handleDeleteFeature}
        onClose={closePopups}
      />
    </div>
  );
};

export default Features;